import React, { useState, useEffect} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { apiConfig } from '../aws-config';
import { formatTime } from '../utils';
import { THRESHOLDS, getThresholdStatus } from '../thresholds';
import { metricLabels } from '../metricLabels';
import { colours } from '../colours';

interface QueueMetrics {
  queue_id: string;
  queue_name: string;
  calls_in_queue: number;
  longest_wait_time: number;
  calls_answered: number;
  average_handle_time: number;
  abandoned_calls: number;
  contacts_missed: number;
  queued_time: number;
  total_calls: number;
  abandonment_rate?: number;
  unique_agent_ids?: string[];
  queue_answer_time: number;
}

interface MultiQueueWallboardPageProps {
  signOut?: () => void;
}

const metricBoxBaseStyle: React.CSSProperties = {
  borderRadius: 0,
  userSelect: 'none',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 600,
  boxSizing: 'border-box',
  padding: 14,
  margin: 0, // Let grid gap control spacing
};

const metricNumberStyle: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: 120,
  lineHeight: 1,
};

const metricLabelStyle: React.CSSProperties = {
  fontSize: 13,
  letterSpacing: '0.15em',
  marginTop: 6,
  fontWeight: 600,
};

const bigCallsWaitingNumberStyle: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: 120,
  lineHeight: 1,
};

const bigCallsWaitingLabelStyle: React.CSSProperties = {
  fontSize: 21,
  letterSpacing: '0.25em',
  marginTop: -12,
  fontWeight: 'bold',
};

// CSS for flashing red background
const flashRedAnimation = `
@keyframes flashRed {
  0%, 50%, 100% { background-color: inherit; }
  25%, 75% { background-color: rgba(255, 0, 0, 0.6); }
}
.flash-red {
  animation: flashRed 1.5s infinite;
  background-color: transparent;
}
`;

// Wrapper component to scale wallboard contents dynamically
const BASE_WIDTH = 1200;
const BASE_HEIGHT = 800;  // Increased to accommodate queue list

const WallboardWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const updateScale = () => {
      const scaleX = window.innerWidth / BASE_WIDTH;
      const scaleY = window.innerHeight / BASE_HEIGHT;
      setScale(Math.min(scaleX, scaleY)); // never scale larger than 100%
    };

    window.addEventListener('resize', updateScale);
    updateScale();

    return () => window.removeEventListener('resize', updateScale);
  }, []);

  return (
    <div
      style={{
        width: BASE_WIDTH,
        height: BASE_HEIGHT,
        transformOrigin: 'top center',
        transform: `scale(${scale})`,
        display: 'inline-block',  // Important: inline-block to allow flex centering
        willChange: 'transform',
      }}
    >
      {children}
    </div>
  );
};


// NEW: compare function to shallow compare relevant fields and dedupe unique agent ids
function areMetricsShallowEqual(prev: QueueMetrics[], next: QueueMetrics[]): boolean {
  if (prev.length !== next.length) return false;

  const areArraysEqual = (arr1?: string[], arr2?: string[]) => {
    if (arr1 === arr2) return true;
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return sorted1.every((val, idx) => val === sorted2[idx]);
  };

  for (let i = 0; i < prev.length; i++) {
    if (
      prev[i].calls_answered !== next[i].calls_answered ||
      prev[i].calls_in_queue !== next[i].calls_in_queue ||
      prev[i].abandoned_calls !== next[i].abandoned_calls ||
      prev[i].contacts_missed !== next[i].contacts_missed ||
      prev[i].longest_wait_time !== next[i].longest_wait_time ||
      prev[i].average_handle_time !== next[i].average_handle_time ||
      prev[i].total_calls !== next[i].total_calls ||
      prev[i].queued_time !== next[i].queued_time ||
      !areArraysEqual(prev[i].unique_agent_ids, next[i].unique_agent_ids)
    ) {
      return false;
    }
  }
  return true;
}


const MetricBox: React.FC<{
  number: number | string;
  label: string;
  style?: React.CSSProperties;
  numberStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
}> = ({ number, label, style, numberStyle, labelStyle }) => {
  const { isDark } = useTheme();

  return (
    <div
      style={{
        ...metricBoxBaseStyle,
        backgroundColor: isDark ? '#1e1e1e' : '#fff',
        color: isDark ? '#bbb' : '#444',
        ...style,
      }}
    >
      <div style={{ ...metricNumberStyle, ...numberStyle }}>{number}</div>
      <div style={{ ...metricLabelStyle, ...labelStyle }}>{label}</div>
    </div>
  );
};

const MultiQueueWallboardPage: React.FC<MultiQueueWallboardPageProps> = ({ signOut }) => {
  const { queueIds } = useParams<{ queueIds: string }>();
  const navigate = useNavigate();
  const { isDark, toggleTheme, theme } = useTheme();

  // NEW: useRef to keep track of previous metrics
  //const prevMetricsRef = useRef<QueueMetrics[]>([]);

  const [allMetrics, setAllMetrics] = useState<QueueMetrics[]>([]);
  //const [loading, setLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // NEW: State for current date/time display
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  // NEW: Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const queueIdArray = queueIds ? queueIds.split(',').map(q => q.trim()).filter(Boolean) : [];

  const calculateAggregatedMetrics = (metrics: QueueMetrics[]) => {
    if (!metrics.length) return null;

    const totalCallsAnswered = metrics.reduce((sum, m) => sum + m.calls_answered, 0);
    const totalHandleTime = metrics.reduce(
      (sum, m) => sum + m.average_handle_time * m.calls_answered,
      0,
    );
    const totalAbandonedCalls = metrics.reduce((sum, m) => sum + m.abandoned_calls, 0);
    const totalCallsQueued = metrics.reduce((sum, m) => sum + (m.total_calls || (m.calls_answered + m.abandoned_calls)), 0);
    const abandonmentRate = totalCallsQueued > 0 ? (totalAbandonedCalls / totalCallsQueued) * 100 : 0;

    return {
      queue_id: 'aggregated',
      queue_name: 'Combined Metrics',
      calls_in_queue: metrics.reduce((sum, m) => sum + m.calls_in_queue, 0),
      longest_wait_time: metrics.length > 0
        ? Math.round(
            metrics.reduce((sum, m) => sum + (m.queue_answer_time * m.calls_answered), 0) / 
            metrics.reduce((sum, m) => sum + m.calls_answered, 0)
          )
        : 0,
      calls_answered: totalCallsAnswered,
      average_handle_time:
        totalCallsAnswered > 0 ? Math.round(totalHandleTime / totalCallsAnswered) : 0,
      abandoned_calls: totalAbandonedCalls,
      contacts_missed: metrics.reduce((sum, m) => sum + m.contacts_missed, 0),
      queued_time: Math.max(...metrics.map(m => m.queued_time)),
      total_calls: totalCallsAnswered + totalAbandonedCalls,
      abandonment_rate: abandonmentRate,
    };
  };
  useEffect(() => {
    const fetchAllMetrics = async () => {
      if (!queueIdArray.length) {
        setAllMetrics([]);
        setIsInitialLoading(false);
        return;
      }
    if (isInitialLoading === false) setError(null);
      //setLoading(true);
      //setError(null);

      try {
        const queuesParam = queueIdArray.join(',');
        const url = `${apiConfig.baseUrl}${apiConfig.endpoints.getMetrics()}?queues=${encodeURIComponent(
          queuesParam,
        )}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const results: QueueMetrics[] = await response.json();
        setAllMetrics((prevMetrics) => {
          if (areMetricsShallowEqual(prevMetrics, results)) {
            return prevMetrics;
          }
          return results;
        });
      } catch (err) {
        console.error('Error fetching metrics:', err);
        setError('Failed to load metrics');
      } finally {
        if (isInitialLoading) setIsInitialLoading(false);  // NEW: turn off loading only on first fetch
      }
    };

    fetchAllMetrics();
    const interval = setInterval(fetchAllMetrics, 5000);
    return () => clearInterval(interval);
  }, [queueIds]);

const [flashRed, setFlashRed] = React.useState(false);
React.useEffect(() => {
  const targetQueueId = 'None';  // Replace with the actual queue ID

  const targetQueue = allMetrics.find(m => m.queue_id === targetQueueId);

  setFlashRed(!!(targetQueue?.calls_in_queue ?? 0));
}, [allMetrics]);

  if (isInitialLoading)                                    // NEW: Show loading only on first load
    return <div style={{ padding: 20, textAlign: 'center' }}>Loading metrics...</div>;
  if (error)
    return <div style={{ padding: 20, color: 'red', textAlign: 'center' }}>{error}</div>;

  const aggregatedMetrics = calculateAggregatedMetrics(allMetrics);

  if (!aggregatedMetrics) {
    return <div style={{ padding: 20, textAlign: 'center' }}>No metrics available</div>;
  }

  const allAgentIds = allMetrics.flatMap(m => m.unique_agent_ids ?? []);
  const uniqueAgentIdsSet = new Set(allAgentIds);
  const uniqueAgentsCount = uniqueAgentIdsSet.size;

  const statusCallsInQueue = getThresholdStatus(aggregatedMetrics!.calls_in_queue ?? 0, THRESHOLDS.callsInQueue);
  const statusUniqueAgentsCount = getThresholdStatus(uniqueAgentsCount, THRESHOLDS.uniqueAgentsCount, true);
  const statusLongestWaitTime = getThresholdStatus(aggregatedMetrics!.longest_wait_time ?? 0, THRESHOLDS.longestWaitTime);
  const statusContactsMissed = getThresholdStatus(aggregatedMetrics!.contacts_missed ?? 0, THRESHOLDS.contactsMissed);
  const statusAbandonedCalls = getThresholdStatus(aggregatedMetrics!.abandoned_calls ?? 0, THRESHOLDS.abandonedCalls);
  const statusAverageTalkTime = getThresholdStatus(aggregatedMetrics!.average_handle_time ?? 0, THRESHOLDS.averageHandleTime);
  const statusAbandonmentRate = getThresholdStatus(aggregatedMetrics!.abandonment_rate ?? 0, THRESHOLDS.abandonmentRate);

  // NEW: Format date and time for display
  const formattedDate = currentDateTime.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const formattedTime = currentDateTime.toLocaleTimeString('en-GB');


  return (
    <>
      {/* Flashing red animation CSS */}
      <style>{flashRedAnimation}</style>
      <div
        style={{
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          backgroundColor: isDark 
            ? colours.dark.pageBackground 
            : colours.light.pageBackground,
          color: isDark 
            ? colours.dark.boxText 
            : colours.light.boxText,
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <WallboardWrapper>
          <div
            className={flashRed ? 'flash-red' : ''}
            style={{
              padding: 20,
              minHeight: BASE_HEIGHT,
              maxWidth: BASE_WIDTH,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {/* Header and buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 30,
                alignItems: 'center',
              }}
            >
              {/* NEW: Logo with date/time */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <img src={isDark ? "/Newcastle-City-Council-3-1.webp" : "/ClientLogo-NewcastleCityCouncil.png"} alt="Company Logo" style={{ height: isDark ? 50 : 47, width: 'auto', alignSelf: 'flex-start' }} />
                <div style={{ fontSize: 14, color: theme.textSecondary }}>
                  {formattedDate} - {formattedTime}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  onClick={toggleTheme}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: theme.surface,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 0,
                    cursor: 'pointer',
                  }}
                >
                  {isDark ? '☀️ Light' : '🌙 Dark'}
                </button>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: theme.textSecondary,
                    color: 'white',
                    border: 'none',
                    borderRadius: 0,
                    cursor: 'pointer',
                  }}
                >
                  ← Back to Queue Selection
                </button>
                {signOut && (
                  <button
                    onClick={signOut}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: theme.danger,
                      color: 'white',
                      border: 'none',
                      borderRadius: 0,
                      cursor: 'pointer',
                    }}
                  >
                    Sign Out
                  </button>
                )}
              </div>
            </div>

            {/* Metrics Grid */}
            {aggregatedMetrics ? (
              <>
              <div
                style={{
                  borderRadius: 0,
                  padding: 0,
                  display: 'grid',
                  maxWidth: 1200,
                  gridTemplateColumns: '1fr 1fr 0.5fr 0.5fr',
                  gridTemplateRows: '100px 100px 100px 100px 100px',
                  gap: '10px 14px',
                  gridTemplateAreas: `
                    "callsWaiting agentsOnline missedCalls abandonedCalls"
                    "callsWaiting agentsOnline missedCalls abandonedCalls"
                    "callsWaiting avgWaitTime answeredCalls totalCalls"
                    "callsWaiting avgTalkTime answeredCalls totalCalls"
                    "longestWait longestWait abandonmentRate abandonmentRate"
                    "newMetric newMetric newMetric newMetric"
                  `,
                  color: isDark ? colours.dark.boxText : colours.light.boxText,
                  userSelect: 'none',
                  backgroundColor: colours[isDark ? 'dark' : 'light'].gridBackground,
                }}
              >
                <div
                  style={{
                    gridArea: 'callsWaiting',
                    borderRadius: 0,
                     backgroundColor: statusCallsInQueue === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxBackground : colours[isDark ? 'dark' : 'light'].boxBackground,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                    paddingBottom: 10,
                    margin: 0,
                    aspectRatio: '1 / 1',
                    minHeight: '100%',
                    color: statusCallsInQueue === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxText : colours[isDark ? 'dark' : 'light'].bigCallsNumber,
                    fontSize: 'clamp(3rem, 8vw, 7rem)',
                  }}
                >
                  <div style={{ ...bigCallsWaitingNumberStyle, color: statusCallsInQueue === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxText : colours[isDark ? 'dark' : 'light'].bigCallsNumber, }}>
                    {aggregatedMetrics.calls_in_queue}
                  </div>
                  <div style={{ ...bigCallsWaitingLabelStyle, color: statusCallsInQueue === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxLabelText : colours[isDark ? 'dark' : 'light'].bigCallsLabel,}}>
                    {metricLabels.callsWaiting}
                  </div>
                </div>

                <MetricBox
                  number={uniqueAgentsCount}
                  label={metricLabels.agentsOnline}
                  style={{
                    gridArea: 'agentsOnline',
                    gridRowEnd: 'span 2',
                    backgroundColor: statusUniqueAgentsCount === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxBackground : colours[isDark ? 'dark' : 'light'].boxBackground,
                  }}
                  numberStyle={{ fontSize: 'clamp(3rem, 8vw, 7rem)', color: statusUniqueAgentsCount === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxText : colours[isDark ? 'dark' : 'light'].boxTextSecondary, 
                  }}
                  labelStyle={{ color: statusUniqueAgentsCount === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxLabelText : colours[isDark ? 'dark' : 'light'].boxTextSecondary, 
                  }}
                />

                <MetricBox
                  number={aggregatedMetrics.contacts_missed}
                  label={metricLabels.missedCalls}
                  style={{
                    gridArea: 'missedCalls',
                    gridRowEnd: 'span 2',
                    aspectRatio: '1 / 1',
                    backgroundColor: statusContactsMissed === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxBackground : colours[isDark ? 'dark' : 'light'].boxBackground,
                  }}
                  numberStyle={{ fontSize: 'clamp(2rem, 5vw, 4rem)', color: statusContactsMissed === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxText : colours[isDark ? 'dark' : 'light'].boxTextSecondary, 

                  }}
                  labelStyle={{ color: statusContactsMissed === 'critical' ? colours[isDark ? 'dark' : 'light'].criticalBoxLabelText : colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                />

                <MetricBox
                  number={aggregatedMetrics.abandoned_calls}
                  label={metricLabels.abandonedCalls}
                  style={{
                    gridArea: 'abandonedCalls',
                    gridRowEnd: 'span 2',
                    aspectRatio: '1 / 1',
                    backgroundColor: statusAbandonedCalls === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxBackground
                      : colours[isDark ? 'dark' : 'light'].boxBackground,
                  }}
                  numberStyle={{
                    fontSize: 'clamp(2rem, 5vw, 4rem)',
                    color: statusAbandonedCalls === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxText
                      : colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                  labelStyle={{
                    color: statusAbandonedCalls === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxLabelText
                      : colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                />

                <MetricBox
                  number={aggregatedMetrics.calls_answered}
                  label={metricLabels.answeredCalls}
                  style={{ gridArea: 'answeredCalls', gridRowEnd: 'span 2', aspectRatio: '1 / 1' }}
                  numberStyle={{
                    fontSize: 'clamp(2rem, 5vw, 4rem)',
                    color: colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                  labelStyle={{
                    color: colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                />

                <MetricBox
                  number={aggregatedMetrics.total_calls}
                  label={metricLabels.totalCalls}
                  style={{ gridArea: 'totalCalls', gridRowEnd: 'span 2', aspectRatio: '1 / 1' }}
                  numberStyle={{
                    fontSize: 'clamp(2rem, 5vw, 4rem)',
                    color: colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                  labelStyle={{
                    color: colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                />

                <MetricBox
                  number={formatTime(aggregatedMetrics.longest_wait_time)}
                  label={metricLabels.avgWaitTime}
                  style={{
                    gridArea: 'avgWaitTime',
                    backgroundColor: statusLongestWaitTime === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxBackground
                      : colours[isDark ? 'dark' : 'light'].boxBackground,
                  }}
                  numberStyle={{
                    fontSize: 'clamp(1.5rem, 3vw, 3rem)',
                    color: statusLongestWaitTime === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxText
                      : colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                  labelStyle={{
                    color: statusLongestWaitTime === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxLabelText
                      : colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                />

                <MetricBox
                  number={formatTime(aggregatedMetrics.average_handle_time)}
                  label={metricLabels.avgTalkTime}
                  style={{
                    gridArea: 'avgTalkTime',
                    backgroundColor: statusAverageTalkTime === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxBackground
                      : colours[isDark ? 'dark' : 'light'].boxBackground,
                  }}
                  numberStyle={{
                    fontSize: 'clamp(1.5rem, 3vw, 3rem)',
                    color: statusAverageTalkTime === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxText
                      : colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                  labelStyle={{
                    color: statusAverageTalkTime === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxLabelText
                      : colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                />

                <MetricBox
                  number={formatTime(aggregatedMetrics.queued_time)}
                  label={metricLabels.longestWait}
                  style={{
                    gridArea: 'longestWait',
                    gridColumn: '1 / span 2',
                    padding: 14,
                    fontWeight: 600,
                    fontSize: 20,
                    color: colours[isDark ? 'dark' : 'light'].boxText,
                    userSelect: 'none',
                    borderRadius: 0,
                    backgroundColor: colours[isDark ? 'dark' : 'light'].boxBackground,
                  }}
                  numberStyle={{
                    fontSize: 30,
                    color: colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                  labelStyle={{
                    color: colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                />

                <MetricBox
                  number={`${aggregatedMetrics.abandonment_rate.toFixed(1)}%`}
                  label={metricLabels.abandonmentRate}
                  style={{
                    gridArea: 'abandonmentRate',
                    borderRadius: 0,
                    padding: 14,
                    fontWeight: 600,
                    fontSize: 20,
                    color: colours[isDark ? 'dark' : 'light'].boxText,
                    userSelect: 'none',
                    backgroundColor: statusAbandonmentRate === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxBackground
                      : colours[isDark ? 'dark' : 'light'].boxBackground,
                  }}
                  numberStyle={{
                    fontSize: 30,
                    color: statusAbandonmentRate === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxText
                      : colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                  labelStyle={{
                    color: statusAbandonmentRate === 'critical'
                      ? colours[isDark ? 'dark' : 'light'].criticalBoxLabelText
                      : colours[isDark ? 'dark' : 'light'].boxTextSecondary,
                  }}
                />
              </div>

              {/* NEW: Queue List at bottom */}
              <div
                style={{
                  marginTop: 10,
                  padding: 8,
                  backgroundColor: isDark ? colours.dark.boxBackground : colours.light.boxBackground,
                  borderRadius: 0,
                  maxWidth: 1200,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 600, color: theme.textSecondary, whiteSpace: 'nowrap' }}>
                  Queues ({allMetrics.length}):
                </div>
                <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, overflow: 'hidden' }}>
                  {allMetrics.map((metric) => (
                    <div
                      key={metric.queue_id}
                      style={{
                        padding: '3px 10px',
                        backgroundColor: isDark ? colours.dark.pageBackground : colours.light.pageBackground,
                        color: theme.text,
                        fontSize: 9,
                        borderRadius: 12,
                        border: `1px solid ${theme.border}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {metric.queue_name}
                    </div>
                  ))}
                </div>
              </div>
              </>
            ) : (
              <div style={{ padding: 20, textAlign: 'center' }}>No metrics available</div>
            )}
          </div>
        </WallboardWrapper>
      </div>
    </>
  );
};

export default MultiQueueWallboardPage;